import React, { useCallback, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import {
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableContainer,
  Typography
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EditMany } from "components/Measure-data/Edit-many/EditMany";
import { useFormatDateHook, useModals } from "hooks";
import { uniqBy } from "lodash";
import { IApiServiceError, useDealActions, useDateFormatSelect } from "store";
import { useDealDefaultCurrencySelect } from "store/selectors/deal-selectors/deal-selectors";
import { CurrencyType, defaultWideFilters, WideEditState, WideFilters, WideRow } from "store/types";

import { buildWideData, filterSplitDetailsByDataIds, filterSplitDetailsByInvestor, mergeSplitDetails } from "./helpers";
import { FixedHeaderCell, HeaderCell } from "./styles";
import { WideFiltersDrawer } from "./WideFiltersDrawer";
import { WideTableRow } from "./WideTableRow";

interface DealWideProps {
  dealId: number | undefined;
  setIsEmpty?: (value: boolean) => void;
}

export const DealWide: React.FC<DealWideProps> = ({ dealId, setIsEmpty }) => {
  const {
    getMeasureDataTableAction,
    getSplitDetailsAction,
    getDealRoundsWithInvestorsAction,
    deleteMeasureDataAction
  } = useDealActions();
  const { deleteModal, errorModal } = useModals();
  const queryClient = useQueryClient();
  const currency = (useDealDefaultCurrencySelect() || "USD") as CurrencyType;
  const formatDate = useFormatDateHook();
  const dateFormat = useDateFormatSelect();

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<WideFilters>(defaultWideFilters);
  const [editState, setWideEditState] = useState<WideEditState | null>(null);

  // ── Fetch flat monitoring data ──────────────────────────────────────────────
  const { data: tableData, isLoading } = useQuery(
    ["deal-wide-data", dealId],
    () =>
      getMeasureDataTableAction({
        per_page: 10000,
        showTopInvestors: true,
        filters: { deals: [{ id: dealId!, title: "" }], hideDeleted: true },
        sort: { order: "asc", orderBy: "date" }
      }),
    {
      enabled: !!dealId,
      onSuccess: (d) => setIsEmpty?.(d.rows.length === 0)
    }
  );

  // ── Fetch rounds + investors for filter options ─────────────────────────────
  const { data: dealRoundsData = [] } = useQuery(
    ["deal-rounds-investors-wide", dealId],
    () => getDealRoundsWithInvestorsAction({ dealId: dealId!, with_parent_round: true }),
    { enabled: !!dealId }
  );

  // ── Derive investor / round options for filter drawer ──────────────────────
  const investorOptions = useMemo(
    () =>
      uniqBy(
        dealRoundsData.flatMap((r) => r.investors.map((inv) => ({ id: String(inv.id), name: inv.name }))),
        "id"
      ),
    [dealRoundsData]
  );

  const roundOptions = useMemo(
    () => dealRoundsData.map((r) => ({ id: String(r.round_id), name: r.round_name })),
    [dealRoundsData]
  );

  // ── Build wide rows ─────────────────────────────────────────────────────────
  const { wideRows, measureColumns } = useMemo(() => {
    if (!tableData?.rows?.length) return { wideRows: [], measureColumns: [] };
    return buildWideData(tableData.rows);
  }, [tableData]);

  // ── Apply client-side filters ───────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    let rows = wideRows;

    if (filters.dateFrom) {
      const from = filters.dateFrom.slice(0, 10);
      rows = rows.filter((r) => r.date >= from);
    }
    if (filters.dateTo) {
      const to = filters.dateTo.slice(0, 10);
      rows = rows.filter((r) => r.date <= to);
    }
    if (filters.rounds.length > 0) {
      const roundNames = new Set(filters.rounds.map((r) => r.name));
      rows = rows.filter((r) => roundNames.has(r.round));
    }
    if (filters.investors.length > 0) {
      const investorNames = new Set(filters.investors.map((i) => i.name));
      rows = rows.flatMap((r) => {
        if (!r.isAggregated) {
          return investorNames.has(r.investor ?? "") ? [r] : [];
        }
        const matchingChildren = r.children.filter((c) =>
          investorNames.has(c.investor ?? "")
        );
        if (matchingChildren.length === 0) return [];
        // Keep parent with only the matching investor children visible
        return [{ ...r, children: matchingChildren }];
      });
    }

    return rows;
  }, [wideRows, filters]);

  const activeFilterCount =
    (filters.dateFrom || filters.dateTo ? 1 : 0) +
    filters.investors.length +
    filters.rounds.length;

  // ── Column visibility ───────────────────────────────────────────────────────
  const shownMeasures = useMemo(
    () =>
      filters.visibleMeasures.length === 0
        ? measureColumns
        : measureColumns.filter((m) => !filters.visibleMeasures.includes(m)),
    [measureColumns, filters.visibleMeasures]
  );

  // ── Edit handlers ───────────────────────────────────────────────────────────
  const openEdit = useCallback(
    async (row: WideRow, measure?: string) => {
      if (!dealId || !row.batchIds.length) return;
      try {
        const allDetails = await Promise.all(
          row.batchIds.map((batchId) =>
            getSplitDetailsAction({ deal_id: dealId, batch_id: batchId })
          )
        );
        let result = mergeSplitDetails(allDetails);

        // Cell click: narrow to only the clicked measure's split group
        if (measure) {
          const cellDataIds = row.isAggregated
            ? new Set(
                row.children
                  .map((c) => c.cells[measure]?.dataId)
                  .filter((id): id is number => !!id && id > 0)
              )
            : (() => {
                const id = row.cells[measure]?.dataId;
                return id && id > 0 ? new Set([id]) : new Set<number>();
              })();

          if (cellDataIds.size > 0) {
            result = filterSplitDetailsByDataIds(result, cellDataIds);
          }
        }

        // Investor row: narrow to that investor's data
        if (!row.isAggregated && row.investor) {
          result = filterSplitDetailsByInvestor(result, row.investor);
        }

        setWideEditState({ splitDetails: result, batchId: row.batchIds[0], investorName: row.investor });
      } catch (err) {
        errorModal({ msg: (err as IApiServiceError)?.message });
      }
    },
    [dealId, getSplitDetailsAction, errorModal]
  );

  const handleEditRow = useCallback((row: WideRow) => openEdit(row), [openEdit]);
  const handleEditCell = useCallback((row: WideRow, measure: string) => openEdit(row, measure), [openEdit]);

  const handleEditClose = useCallback(() => {
    setWideEditState(null);
    queryClient.invalidateQueries(["deal-wide-data", dealId]);
  }, [dealId, queryClient]);

  // ── Delete handler ──────────────────────────────────────────────────────────
  const handleDeleteRow = useCallback(
    (row: WideRow) => {
      const ids = row.allDataIds;
      if (!ids.length) return;

      const subject = row.isAggregated
        ? `batch entry for ${row.date}`
        : `${row.investor ?? "entry"} on ${row.date}`;

      deleteModal({
        element: "entry",
        message: `This will delete ${ids.length} measure entr${ids.length === 1 ? "y" : "ies"} for ${subject}. This action cannot be undone.`,
        action: async () => {
          await Promise.all(ids.map((id) => deleteMeasureDataAction(id)));
          queryClient.invalidateQueries(["deal-wide-data", dealId]);
        }
      });
    },
    [deleteModal, deleteMeasureDataAction, dealId, queryClient]
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Stack gap={1} sx={{ p: 2 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
        ))}
      </Stack>
    );
  }

  if (!wideRows.length) {
    return (
      <Typography variant="body2" sx={{ color: "#71A0B2", p: 2 }}>
        No monitoring data available.
      </Typography>
    );
  }

  return (
    <>
      {/* Toolbar */}
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center", height: 26, mb: "24px", gap: "10px" }}
      >
        <Stack direction="row" sx={{ gap: "14px", alignItems: "center" }}>
          <Button
            size="sm"
            variant="outlined"
            startIcon={<FilterAltIcon />}
            onClick={() => setFiltersOpen(true)}
            sx={{
              height: "26px",
              fontSize: "15px",
              textTransform: "none",
              fontWeight: 400,
              backgroundColor: activeFilterCount > 0 ? "#dae0e2" : "transparent"
            }}
          >
            Filters
          </Button>

          {filters.investors.map((inv) => (
            <Chip
              key={inv.id}
              label={inv.name}
              size="small"
              onDelete={() =>
                setFilters((prev) => ({ ...prev, investors: prev.investors.filter((i) => i.id !== inv.id) }))
              }
            />
          ))}
          {filters.rounds.map((r) => (
            <Chip
              key={r.id}
              label={r.name}
              size="small"
              onDelete={() =>
                setFilters((prev) => ({ ...prev, rounds: prev.rounds.filter((x) => x.id !== r.id) }))
              }
            />
          ))}
          {(filters.dateFrom || filters.dateTo) && (
            <Chip
              label={`${filters.dateFrom ? formatDate({ date: filters.dateFrom, format: dateFormat }) : "…"} – ${filters.dateTo ? formatDate({ date: filters.dateTo, format: dateFormat }) : "…"}`}
              size="small"
              onDelete={() => setFilters((prev) => ({ ...prev, dateFrom: null, dateTo: null }))}
            />
          )}
          {activeFilterCount > 0 && (
            <Button
              onClick={() => setFilters(defaultWideFilters)}
              startIcon={<CloseIcon className="icon" />}
              sx={{ height: "26px", fontSize: "15px", textTransform: "none", fontWeight: 400 }}
            >
              Clear All Filters
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{ border: "1px solid #DBE8F5", borderRadius: "8px", boxShadow: "none", flexGrow: 1, overflowX: "auto" }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ height: 40 }}>
              <FixedHeaderCell>Date</FixedHeaderCell>
              <FixedHeaderCell>Round</FixedHeaderCell>
              <FixedHeaderCell>Investor</FixedHeaderCell>
              <FixedHeaderCell>Bank Account</FixedHeaderCell>
              {shownMeasures.map((m) => (
                <HeaderCell key={m}>{m}</HeaderCell>
              ))}
              <HeaderCell sx={{ width: 72 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row) => (
              <WideTableRow
                key={row.key}
                row={row}
                measureColumns={measureColumns}
                visibleMeasures={filters.visibleMeasures}
                depth={0}
                formatDate={formatDate}
                onEditRow={handleEditRow}
                onEditCell={handleEditCell}
                onDeleteRow={handleDeleteRow}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Filters Drawer */}
      <WideFiltersDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filters}
        onApply={setFilters}
        investorOptions={investorOptions}
        roundOptions={roundOptions}
        measureOptions={measureColumns}
      />

      {/* Edit Drawer */}
      {editState && (
        <EditMany
          splitDetails={editState.splitDetails}
          deal_id={dealId}
          batch_id={editState.batchId}
          defaultCurrency={currency}
          handleClose={handleEditClose}
        />
      )}

    </>
  );
};
